import { DownloadOutlined, SaveOutlined } from '@ant-design/icons';
import { useMutation, useQuery as useReactQuery } from '@tanstack/react-query';
import { App, Badge, Button, Form, Spin, Tabs, Tooltip } from 'antd';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { useLocation, useNavigate } from 'react-router-dom';
import { setAdditional, updateField } from '../../../contextRedux/AdditionalAct/AdditionalActModel';
import { setCompanies } from '../../../contextRedux/Company/CompanyStore';
import { setDevices } from '../../../contextRedux/DeviceType/DeviceTypeStore';
import { setStatusContractes } from '../../../contextRedux/StatusContract/StatusContractStore';
import { setSystemTypes } from '../../../contextRedux/SystemType/SystemTypeStore';
import { setUsers } from '../../../contextRedux/User/UserStore';
import CompanyService from '../../../services/company/CompanyService';
import ConfigurationService from '../../../services/configuration/ConfigurationService';
import ContractService from '../../../services/contract/ContractService';
import ContractAdditionalActService from '../../../services/contractAdditionalAct/ContractAdditionalActService';
import DeviceTypeService from '../../../services/deviceType/DeviceTypeService';
import SystemTypeService from '../../../services/systemType/SystemTypeService';
import UserService from '../../../services/user/UserService';
import WorkPlaceService from '../../../services/workPlace/WorkPlaceService';
import { RootState } from '../../../store';
import { saveBlobCrossPlatform } from '../../../utils/saveBlobCrossPlatform';
import { Tab } from '../../../utils/tabs';
import AddEquipmentTab from './Tabs/AddEquipmentTab/AddEquipmentTab';
import CompanyDetailsTab from './Tabs/CompanyDetailsTab/CompanyDetailsTab';
import LaborTab from './Tabs/LaborTab/LaborTab';
import SummaryTab from './Tabs/SummaryTab/SummaryTab';

const NewAdditionalAct: FC = () => {
  const isMobile = useMediaQuery({ maxWidth: 720 });
  // const isTab = useMediaQuery({ maxWidth: 992});

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { modal, message } = App.useApp();

  const location = useLocation();

  const useQueryParams = () => {
    return new URLSearchParams(useLocation().search);
  };

  const query = useQueryParams();
  const [id, setId] = useState<string | null>(query.get('id'));

  const reduxAdditionalAct = useSelector((state: RootState) => state?.models?.additionalAct);

  const addedDevices = useSelector((state: RootState) => state?.models?.additionalAct?.contractWorkPlaceItems);

  const [totalContractValue, setTotalContractValue] = useState<number>(0);
  const [version, setVersion] = useState<number>();
  const [fileList, setFileList] = useState<any>([]);

  const isShowAddEquipmentTab: boolean = !!(
    reduxAdditionalAct?.companyId &&
    reduxAdditionalAct?.accountManagerId &&
    reduxAdditionalAct?.accountSaleId &&
    reduxAdditionalAct?.enumContractStatusId &&
    reduxAdditionalAct?.acquisitionType &&
    reduxAdditionalAct?.date &&
    reduxAdditionalAct?.dateStart &&
    reduxAdditionalAct?.dateEnd &&
    reduxAdditionalAct?.registrationNumber
  );

  const typePeriod = ['lunar', 'trimestrial', 'semestrial', 'anual', 'la cerere'];
  const userRols = 'sales';

  const { data: _accountSales, isFetching: isUsersLoading } = useReactQuery({
    queryKey: ['users'],
    queryFn: () =>
      UserService.search({
        isActive: true,
        rols: userRols,
      }).then((data) => {
        dispatch(setUsers(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    const idFromUrl = query.get('id');
    if (!idFromUrl) {
    }
    setId(idFromUrl);
    dispatch(updateField({ field: 'id', value: idFromUrl }));
  }, [location.search]);

  const {
    data: _contract,
    isFetching: isContractsLoading,
    refetch,
  } = useReactQuery({
    queryKey: ['contractAdditionalActs'],
    queryFn: () =>
      ContractAdditionalActService.search({
        contractId: id,
        isMetadataIncluded: true,
      }).then((data) => {
        dispatch(setAdditional(data?.response[0]));
        setVersion(data?.version);
        return data?.response[0];
      }),
    enabled: !!id,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: companies, isFetching: isCompaniesLoading } = useReactQuery({
    queryKey: ['companies'],
    queryFn: () =>
      CompanyService.search({
        isAgentIncluded: true,
        isWorkPlacesIncluded: true,
        isAccountManagerIncluded: true,
        isActive: true,
      }).then((data) => {
        dispatch(setCompanies(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _status, isFetching: isStatusLoading } = useReactQuery({
    queryKey: ['contractStatuses'],
    queryFn: () =>
      ConfigurationService.searchContractStatus({}).then((data) => {
        dispatch(setStatusContractes(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _systemTypes, isFetching: isSystemTypesLoading } = useReactQuery({
    queryKey: ['systemTypes'],
    queryFn: () =>
      SystemTypeService.search({
        isDeviceTypeIncluded: true,
        isActive: true,
      }).then((data) => {
        dispatch(setSystemTypes(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _deviceTypes, isFetching: isDeviceTypesLoading } = useReactQuery({
    queryKey: ['deviceTypes'],
    queryFn: () =>
      DeviceTypeService.search({
        isSystemTypeIncluded: true,
        isLaborIncluded: true,
        isActive: true,
      }).then((data) => {
        dispatch(setDevices(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { mutate: saveAdditionalActContract, isPending: isSavingAdditionalActPmContract } = useMutation({
    mutationFn: (formData: any) =>
      ContractAdditionalActService.upsert(formData)
        .then(async (response) => {
          const payload = await response.json().catch(() => null);
          if (!response.ok) {
            const backendErrors =
              payload?.errors?.length > 0 ? payload.errors.join(' | ') : `HTTP error! Status: ${response.status}`;
            throw new Error(backendErrors);
          }
          return payload;
        })
        .then((data) => {
          if (data) {
            handleResponse(data);
            setFileList([]);
            navigate(`/service-maintenance/contract/act-additionally/edit?id=${data?.response?.id}`);
            refetch();
          } else {
            console.error('Received data is not in JSON format or JSON parsing failed?.');
          }
        })
        .catch((error) => {
          message.error(error?.message ?? 'Eroare procesare request.');
          console.error('Error processing your request:', error);
        }),
  });

  const onSaveAdditionalAct = () => {
    if (!fileList[0]) {
      message.error('Incarca fisiere!');
      return;
    }

    const formData = new FormData();

    // Append form fields
    formData.append('Id', reduxAdditionalAct?.id);
    formData.append('CompanyId', reduxAdditionalAct?.companyId);
    formData.append('Number', reduxAdditionalAct?.number);
    formData.append('Remarks', reduxAdditionalAct?.remarks);
    formData.append('Date', reduxAdditionalAct?.date);
    formData.append('DateStart', reduxAdditionalAct?.dateStart);
    formData.append('DateEnd', reduxAdditionalAct?.dateEnd);
    formData.append('DatePeriod', reduxAdditionalAct?.datePeriod);
    formData.append('AcquisitionType', reduxAdditionalAct?.acquisitionType);
    formData.append('IsExpedite', reduxAdditionalAct?.isExpedite);
    formData.append('HasAutomaticExtension', reduxAdditionalAct?.hasAutomaticExtension);
    formData.append('RegistrationNumber', reduxAdditionalAct?.registrationNumber);
    formData.append('Intervention', reduxAdditionalAct?.intervention);
    formData.append('OfferId', reduxAdditionalAct?.offerId);
    formData.append('Status', reduxAdditionalAct?.enumContractStatus);
    formData.append('EnumContractStatusId', reduxAdditionalAct?.enumContractStatusId);
    formData.append('InterventionPerSystemType', reduxAdditionalAct?.equipmentTab?.InterventionPerSystemType);
    formData.append('ContractWorkPlaceItems', JSON.stringify(reduxAdditionalAct?.contractWorkPlaceItems));
    formData.append('ContractLaborExtras', JSON.stringify(reduxAdditionalAct?.contractLaborExtras));

    formData.append('Files', fileList[0]);
    saveAdditionalActContract(formData);
  };

  const filterAndGroupData = (data: any) => {
    const result: any = {};

    data.forEach((item: any) => {
      const { workPlace, workPlaceId, systemTypeId, deviceTypeId, piece, isAnotherProvider, details } = item;

      if (!result[workPlaceId]) {
        result[workPlaceId] = {
          workPlace: workPlace,
          workPlaceId: workPlaceId,
          devices: [],
        };
      }

      result[workPlaceId].devices.push({
        systemTypeId,
        deviceTypeId,
        piece,
        isAnotherProvider,
        details,
      });
    });

    return Object.values(result);
  };

  const { mutate: updateWorkPace } = useMutation({
    mutationFn: (formData: any) => WorkPlaceService.upsert(formData.id, formData),
    onSuccess: () => {},
    onError: () => {
      message.error('Eroare procesare request.');
    },
  });

  const handleSaveContract = async () => {
    if (!fileList[0]) {
      message.error('Incarca fisiere!');
      return;
    }
    try {
      modal.confirm({
        title: 'Confirm Update',
        content: 'Sigur doriți să actualizați punctele de lucru cu configurația curentă?',
        okText: 'Da',
        cancelText: 'Nu',
        onOk: async () => {
          const workPlaceItems = filterAndGroupData(reduxAdditionalAct?.contractWorkPlaceItems);
          workPlaceItems.forEach((workPlaceItem: any) => {
            const formData = {
              ...workPlaceItem?.workPlace,
              agents:
                companies
                  .find((c: any) => c.id == workPlaceItem?.workPlace?.companyId)
                  .workPlaces.find((wp: any) => wp.id == workPlaceItem?.workPlace.id)?.agents ?? [],
              contactPersons:
                companies
                  .find((c: any) => c.id == workPlaceItem?.workPlace?.companyId)
                  .workPlaces.find((wp: any) => wp.id == workPlaceItem?.workPlace?.id)?.contactPersons ?? [],
              workPlaceItems: workPlaceItem?.devices.map((d: any) => {
                return {
                  id: '',
                  workPlaceId: workPlaceItem?.workPlaceId,
                  deviceTypeId: d?.deviceTypeId,
                  systemTypeId: d?.systemTypeId,
                  details: d?.details ?? '',
                  piece: d?.piece,
                  metadata: '{}',
                  isAnotherProvider: d?.isAnotherProvider ?? false,
                };
              }),
            };
            updateWorkPace(formData);
          });
          await onSaveAdditionalAct();
        },
        onCancel: async () => {
          await onSaveAdditionalAct();
        },
      });
    } catch (errorInfo: any) {
      message.error(errorInfo?.errorFields[0]?.errors);
    }
  };

  const handleResponse = (data: any) => {
    if (data?.errors?.length > 0) {
      data?.errors?.forEach((item: any) => {
        message.error(item);
      });
    } else {
      message.success(
        <span>
          <span className='font-weight-bold'>{data?.response?.number}</span> a fost {!!id ? 'modificat' : 'adaugat'} cu
          success
        </span>
      );
    }
  };

  const { mutate: downloadAdditonalFile, isPending: isSavingAdditionalActFile } = useMutation({
    mutationFn: () =>
      ContractService.downloadAdditionalFile(id).then((response) => {
        // Extract filename from Content-Disposition header
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'file.zip'; // Default filename if header is missing

        if (contentDisposition) {
          const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
          const matches = filenameRegex.exec(contentDisposition);
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, '');
          }
        }

        return response
          .blob()
          .then((blob: any) => {
            saveBlobCrossPlatform(blob, filename);
          })
          .catch((error: any) => {
            console.error('Error processing your request:', error);
          });
      }),
  });

  useEffect(() => {
    if (addedDevices?.length > 0) {
      const totalLabor = Number.parseFloat(
        addedDevices
          .reduce(
            (total: any, item: any) =>
              total +
              item.laborPrice *
                (item.period === 'lunar'
                  ? 12
                  : item.period === 'trimestrial'
                  ? 4
                  : item.period === 'semestrial'
                  ? 2
                  : item.period === 'anual'
                  ? 1
                  : 1),
            0
          )
          .toFixed(2)
      );

      if (addedDevices?.length > 0 && addedDevices.some((ad: any) => ad.period === 'la cerere')) {
        const groupedDevices = addedDevices.reduce((acc: any, device: any) => {
          const key = `${device?.systemType.name}-${device?.unitMeasure}-${device?.period}-${device?.workPlaceId}`;

          if (!acc[key]) {
            acc[key] = {
              systemType: device?.systemType.name,
              periodicity: device?.period,
              unitMeasure: device?.unitMeasure,
              interventionPriceOnRequest: 0,
              totalPrice: 0,
              workPlaceId: device?.workPlaceId,
              count: 0,
            };
          }

          acc[key].interventionPriceOnRequest += device?.interventionPriceOnRequest ?? 0;
          acc[key].totalPrice += device?.laborPrice;
          acc[key].count += 1;

          return acc;
        }, {});

        // Calculate the averages and transform into an array
        const result = Object.values(groupedDevices).map((group: any) => ({
          ...group,
          interventionPriceOnRequest: group?.interventionPriceOnRequest / group?.count,
        }));

        const totalInterventionPriceOnRequest = result.reduce(
          (acc, group) => acc + group?.interventionPriceOnRequest,
          0
        );
        setTotalContractValue(totalInterventionPriceOnRequest + (reduxAdditionalAct?.isExpedite == true ? 250 : 0));
      } else {
        setTotalContractValue(totalLabor + (reduxAdditionalAct?.isExpedite == true ? 250 : 0));
      }
    } else {
      setTotalContractValue(0);
    }
  }, [addedDevices, reduxAdditionalAct.isExpedite]);
  const tabs: (Tab | false)[] = [
    {
      key: 'companyTab',
      label: 'Detalii Client',
      children: <CompanyDetailsTab form={form} fileList={fileList} setFileList={setFileList} />,
    },
    isShowAddEquipmentTab && {
      key: 'deviceTab',
      label: 'Adaugare Echipament',
      children: <AddEquipmentTab />,
    },
    addedDevices?.length > 0 && {
      key: 'summaryTab',
      label: (
        <>
          Vizualizare Echipamente Adaugate
          <Badge
            color='blue'
            style={{ marginLeft: '5px' }}
            count={addedDevices?.length > 0 ? addedDevices?.length : ''}
          ></Badge>
        </>
      ),
      children: <SummaryTab typePeriod={typePeriod} />,
    },
    addedDevices?.length! > 0 &&
      addedDevices?.some((ad) => ad?.period !== 'la cerere') && {
        key: 'manoperaTab',
        label: 'Manopera',
        children: <LaborTab />,
      },
  ];

  const filteredTabs: Tab[] = tabs?.filter((tab): tab is Tab => tab !== false);

  if (
    isSavingAdditionalActPmContract ||
    isSavingAdditionalActFile ||
    isContractsLoading ||
    isCompaniesLoading ||
    isStatusLoading ||
    isSystemTypesLoading ||
    isDeviceTypesLoading ||
    isUsersLoading
  ) {
    return <Spin size='large' className='spinner' />;
  }

  return (
    <>
      <div className='topLine'>
        <h2>
          {id
            ? `Act Aditional Mentenanta - V${version} - Nr. Contract ${reduxAdditionalAct?.number}`
            : 'Act Aditional Mentenanta Nou'}
        </h2>
        <div className='totalLine'>
          <h3>
            {reduxAdditionalAct?.isExpedite ? (
              <span style={{ color: 'red' }}>Urgent Total - {totalContractValue}</span>
            ) : (
              'Total - ' + totalContractValue
            )}
          </h3>
          <div className='buttons'>
            <Button className='saveBtn' onClick={() => handleSaveContract()}>
              {isMobile ? (
                <Tooltip title='Salveaza Act Aditional'>
                  <SaveOutlined />
                </Tooltip>
              ) : (
                'Salveaza Act Aditional'
              )}
            </Button>
            <Button className='downloadBtn' onClick={() => downloadAdditonalFile()}>
              {isMobile ? (
                <Tooltip title='Descarca Acte Aditionale ZIP'>
                  <DownloadOutlined />
                </Tooltip>
              ) : (
                <>
                  <DownloadOutlined /> Descarca Acte Aditionale ZIP
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
      <Tabs
        defaultActiveKey='1'
        items={filteredTabs}
        // tabPosition={isMobile || isTab ? 'top' : 'left'}
        className='tabs'
        tabBarStyle={{ backgroundColor: '#DADBDD' }}
        tabBarGutter={18}
      />
    </>
  );
};

export default NewAdditionalAct;
